import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import {
  deleteArchivedClient,
  fetchArchivedClients,
  restoreClient,
} from "../../../services/ClientsService";
import "./Clients.css";

const PAGE_SIZE = 10;

const getInitials = (name = "") => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (parts[0] || "?").slice(0, 2).toUpperCase();
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ArchivedClients = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState("");

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchArchivedClients({ search: search.trim() });
      setClients(Array.isArray(data?.clients) ? data.clients : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || t("clientsLoadError"));
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [search, t]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadClients();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loadClients]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(clients.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageClients = clients.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const rangeStart =
    clients.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, clients.length);

  const handleRestore = async (client) => {
    if (!client?.id || busyId) return;
    const ok = await swal({
      title: t("areYouSure") || "Are you sure?",
      text: t("clientsRestoreConfirm"),
      icon: "info",
      buttons: [t("cancel"), t("clientsRestore")],
    });
    if (!ok) return;

    setBusyId(client.id);
    try {
      await restoreClient(client.id);
      toast.success(t("clientsRestored"));
      await loadClients();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          t("clientsRestoreError")
      );
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (client) => {
    if (!client?.id || busyId) return;
    const ok = await swal({
      title: t("areYouSure") || "Are you sure?",
      text: t("clientsDeleteConfirm"),
      icon: "warning",
      dangerMode: true,
      buttons: [t("cancel"), t("clientsDelete")],
    });
    if (!ok) return;

    setBusyId(client.id);
    try {
      await deleteArchivedClient(client.id);
      toast.success(t("clientsDeleted"));
      await loadClients();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          t("clientsDeleteError")
      );
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      <PageTitle
        motherMenu={t("Archived")}
        activeMenu={t("navArchivedClients")}
      />

      <div className="clients-page">
        <header className="clients-hero">
          <div className="clients-hero__copy">
            <span className="clients-hero__mark" aria-hidden="true">
              <i className="fas fa-archive" />
            </span>
            <div className="clients-hero__text">
              <h2 className="clients-hero__title">
                <span className="clients-hero__title-main">
                  {t("clientsArchivedTitle")}
                </span>
                <span className="clients-hero__title-accent" aria-hidden="true" />
              </h2>
              <p className="clients-hero__subtitle">
                {t("clientsArchivedSubtitle")}
              </p>
            </div>
          </div>
          <div className="clients-hero__stats">
            <div className="clients-stat">
              <i className="fas fa-users" aria-hidden="true" />
              <div>
                <strong>{clients.length}</strong>
                <span>{t("clientsTotal")}</span>
              </div>
            </div>
          </div>
        </header>

        <section className="clients-table-panel">
          <div className="clients-toolbar">
            <div className="clients-toolbar__controls">
              <div className="clients-search">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("clientsSearchPlaceholder")}
                  aria-label={t("clientsSearchPlaceholder")}
                />
              </div>
              <span className="clients-toolbar__count">
                {clients.length === 0
                  ? `0 ${t("clients").toLowerCase()}`
                  : `${rangeStart}–${rangeEnd} / ${clients.length}`}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="clients-empty">{t("loading")}...</div>
          ) : pageClients.length === 0 ? (
            <div className="clients-empty">
              <span className="clients-empty__icon" aria-hidden="true">
                <i className="fas fa-archive" />
              </span>
              <strong>{t("clientsArchivedEmpty")}</strong>
              <span>{t("clientsArchivedEmptyHint")}</span>
            </div>
          ) : (
            <div className="clients-table-wrap">
              <table className="clients-table is-archived">
                <thead>
                  <tr>
                    <th scope="col">{t("clientsName")}</th>
                    <th scope="col">{t("clientsPhone")}</th>
                    <th scope="col">{t("clientsEmail")}</th>
                    <th scope="col">{t("clientsVisits")}</th>
                    <th scope="col">{t("clientsSource")}</th>
                    <th scope="col">{t("clientsArchivedAt")}</th>
                    <th scope="col" className="is-actions">
                      {t("clientsActions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageClients.map((client) => {
                    const busy = busyId === client.id;
                    return (
                      <tr key={client.id || client._id}>
                        <td>
                          <div className="clients-table__identity">
                            <span
                              className="clients-avatar is-sm"
                              aria-hidden="true"
                            >
                              {getInitials(client.displayName)}
                            </span>
                            <div className="clients-table__name">
                              <strong>{client.displayName || "—"}</strong>
                              {client.country ? (
                                <span>{client.country}</span>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="clients-table__cell-text">
                            {client.phone || "—"}
                          </span>
                        </td>
                        <td>
                          <span className="clients-table__cell-text is-email">
                            {client.email || "—"}
                          </span>
                        </td>
                        <td>
                          <span className="clients-visits-pill">
                            {client.visitCount || 0}
                          </span>
                        </td>
                        <td>
                          <div className="clients-source-badges is-inline">
                            {(client.sources || []).map((source) => (
                              <span
                                key={source}
                                className={`clients-source-badge is-${source}`}
                              >
                                {source === "table"
                                  ? t("clientsSourceTable")
                                  : t("clientsSourceEvent")}
                              </span>
                            ))}
                            {(client.sources || []).length === 0 ? "—" : null}
                          </div>
                        </td>
                        <td>
                          <span className="clients-table__date">
                            {formatDate(client.archivedAt)}
                          </span>
                        </td>
                        <td className="is-actions">
                          <div className="clients-table__actions">
                            <button
                              type="button"
                              className="clients-table__icon-btn is-restore"
                              disabled={busy}
                              onClick={() => handleRestore(client)}
                              aria-label={t("clientsRestore")}
                              title={t("clientsRestore")}
                            >
                              <i className="fas fa-undo" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="clients-table__icon-btn is-delete"
                              disabled={busy}
                              onClick={() => handleDelete(client)}
                              aria-label={t("clientsDelete")}
                              title={t("clientsDelete")}
                            >
                              <i className="fas fa-trash" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && clients.length > PAGE_SIZE ? (
            <div className="clients-pagination is-table">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                {t("previous")}
              </button>
              <span>
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() =>
                  setPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                {t("next")}
              </button>
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
};

export default ArchivedClients;
